import { Box, IconButton, Link, Typography } from "@material-ui/core";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import { BarChart, Settings } from "@material-ui/icons";
import ApartmentIcon from "@material-ui/icons/Apartment";
import HomeIcon from "@material-ui/icons/Home";
import MailIcon from "@material-ui/icons/Mail";
import SearchIcon from "@material-ui/icons/Search";
import * as React from "react";
import { useHistory, useLocation } from "react-router";
import { useRecoilValue } from "recoil";

import { sendEventToAppMeasurementTool, isFirstAccess, AppEventTokenEnum } from "@/utilities/capacitorLogic";
import { UserState } from "@/view_models/atoms";
import { proposalUnreadCount } from "@/view_models/proposalUnreadCounts";

import LogoImage from "@assets/image/logo_gradation01.png";

import { CountBadge } from "../CountBadge";
import { useTopBarStyles } from "../topBarStyles";

export { PC_HEADER_HEIGHT } from "../topBarStyles";

interface NavigationItem {
  name: string;
  url: string;
  icon: React.JSX.Element;
  onClickLink: () => void;
  showUnreadBadge?: boolean;
}

export const ManagementAppBar: React.FC = () => {
  const classes = useTopBarStyles();
  const history = useHistory();
  const location = useLocation();
  const user = useRecoilValue(UserState);
  const unreadCount = useRecoilValue(proposalUnreadCount);

  const onClickLinkMypage = () => {
    sendEventToAppMeasurementTool(
      isFirstAccess(user?.created_at)
        ? AppEventTokenEnum.MypageFirstAccess
        : AppEventTokenEnum.MypageSecondOnwardsAccess
    );
  };

  const onClickLinkManagementOwned = () => {
    sendEventToAppMeasurementTool(
      isFirstAccess(user?.created_at)
        ? AppEventTokenEnum.OwnPropertyManagementFirstAccess
        : AppEventTokenEnum.OwnPropertyManagementSecondOnwardsAccess
    );
  };

  const onClickLinkManagementConsideration = () => {
    sendEventToAppMeasurementTool(
      isFirstAccess(user?.created_at)
        ? AppEventTokenEnum.ConsiderationPropertyManagementFirstAccess
        : AppEventTokenEnum.ConsiderationPropertyManagementSecondOnwardsAccess
    );
  };

  const onClickLinkOffer = () => {
    sendEventToAppMeasurementTool(
      isFirstAccess(user?.created_at) ? AppEventTokenEnum.OfferFirstAccess : AppEventTokenEnum.OfferSecondOnwardsAccess
    );
  };

  const onClickLinkSetting = () => {
    sendEventToAppMeasurementTool(
      isFirstAccess(user?.created_at)
        ? AppEventTokenEnum.SettingFirstAccess
        : AppEventTokenEnum.SettingSecondOnwardsAccess
    );
  };

  const onClickAgentSearchLink = () => {
    sendEventToAppMeasurementTool(
      isFirstAccess(user?.created_at)
        ? AppEventTokenEnum.AgentSearchFirstAccess
        : AppEventTokenEnum.AgentSearchSecondOnwardsAccess
    );
  };

  const navigationItems: NavigationItem[] = [
    {
      name: "ホーム",
      url: "/mypage",
      icon: <HomeIcon />,
      onClickLink: onClickLinkMypage,
    },
    {
      name: "メッセージ",
      url: "/offer",
      icon: <MailIcon />,
      onClickLink: onClickLinkOffer,
      showUnreadBadge: true,
    },
    {
      name: "プランナー",
      url: "/agent_search",
      icon: <SearchIcon />,
      onClickLink: onClickAgentSearchLink,
    },
    {
      name: "保有物件",
      url: "/management/own",
      icon: <ApartmentIcon />,
      onClickLink: onClickLinkManagementOwned,
    },
    {
      name: "検討中物件",
      url: "/management/consideration",
      icon: <BarChart />,
      onClickLink: onClickLinkManagementConsideration,
    },
  ];

  const isActive = (item: NavigationItem) =>
    location.pathname.startsWith(item.url) ||
    (item.url === "/mypage" && ["/usage_guide", "/balance"].includes(location.pathname));

  const onClickNavItem = (item: NavigationItem) => {
    item.onClickLink();
    history.push(item.url);
  };

  return (
    <AppBar position="static" elevation={0} className={classes.appBar}>
      <Toolbar className={classes.toolBar} disableGutters>
        <img src={LogoImage} alt="logo" className={classes.logoImage} />
        <Box component="nav" className={classes.nav}>
          {navigationItems.map((item) => {
            const active = isActive(item);
            return (
              <Link
                underline="none"
                href={item.url}
                key={item.name}
                className={`${classes.navItem} ${active ? classes.navItemActive : ""}`}
                onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                  event.preventDefault();
                  onClickNavItem(item);
                }}
                aria-current={active ? "page" : undefined}
                role="link"
              >
                {item.icon}
                <Typography variant="body2" className={classes.navLabel}>
                  {item.name}
                </Typography>
                {item.showUnreadBadge && <CountBadge count={unreadCount} />}
              </Link>
            );
          })}
        </Box>
        <IconButton
          component="a"
          href="/settings"
          aria-label="設定"
          aria-current={location.pathname.startsWith("/settings") ? "page" : undefined}
          className={`${classes.settings} ${location.pathname.startsWith("/settings") ? classes.navItemActive : ""}`}
          onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
            if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            event.preventDefault();
            onClickLinkSetting();
            history.push("/settings");
          }}
        >
          <Settings />
        </IconButton>
      </Toolbar>
    </AppBar>
  );
};
